import { useState, useEffect } from 'react';
import { Tournament, ValidationError, DEFAULT_TOURNAMENT_RULES, Team } from '@/types';
import { createTournament, updateTournament, getTeams, getParticipatingTeams } from '@/lib/supabase';
import { v4 as uuidv4 } from 'uuid';

interface TournamentFormProps {
  tournament: Tournament | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function TournamentForm({ tournament, onSuccess, onCancel }: TournamentFormProps) {
  const [name, setName] = useState(tournament?.name || '');
  const [startDate, setStartDate] = useState(tournament?.startDate || new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(tournament?.endDate || new Date().toISOString().split('T')[0]);
  const [venue, setVenue] = useState(tournament?.venue || '');
  const [leagueWinningPoint, setLeagueWinningPoint] = useState(tournament?.leagueWinningPoint || DEFAULT_TOURNAMENT_RULES.leagueWinningPoint);
  const [leagueGoldenPoint, setLeagueGoldenPoint] = useState(tournament?.leagueGoldenPoint || DEFAULT_TOURNAMENT_RULES.leagueGoldenPoint);
  const [playoffWinningPoint, setPlayoffWinningPoint] = useState(tournament?.playoffWinningPoint || DEFAULT_TOURNAMENT_RULES.playoffWinningPoint);
  const [playoffGoldenPoint, setPlayoffGoldenPoint] = useState(tournament?.playoffGoldenPoint || DEFAULT_TOURNAMENT_RULES.playoffGoldenPoint);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const isEditing = !!tournament;

  // Fetch all teams and participating teams for editing
  useEffect(() => {
    const loadData = async () => {
      setLoadingTeams(true);
      try {
        const rawTeamsData = await getTeams();
        const teamsData: Team[] = (rawTeamsData ?? []).map((t: any) => ({
          id: t.id,
          name: t.name,
          shortName: t.short_name,
          color: t.color,
          logoUrl: t.logo_url,
          players: (t.players || []).map((p: any) => ({ id: p.id, name: p.name, number: p.number, teamId: p.team_id })),
        }));
        setAllTeams(teamsData);

        // If editing, fetch current participating teams
        if (tournament) {
          const participatingTeams = await getParticipatingTeams(tournament.id);
          setSelectedTeamIds(participatingTeams.map((t: Team) => t.id));
        }
      } catch (err) {
        console.error('Failed to load teams:', err);
      } finally {
        setLoadingTeams(false);
      }
    };
    loadData();
  }, [tournament]);

  const toggleTeamSelection = (teamId: string) => {
    setSelectedTeamIds(prev =>
      prev.includes(teamId)
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  const validate = (): ValidationError[] => {
    const errs: ValidationError[] = [];

    if (!name.trim()) {
      errs.push({ id: uuidv4(), title: 'Name Required', message: 'Tournament name is required.' });
    }

    if (!startDate) {
      errs.push({ id: uuidv4(), title: 'Start Date Required', message: 'Tournament start date is required.' });
    }

    if (!endDate) {
      errs.push({ id: uuidv4(), title: 'End Date Required', message: 'Tournament end date is required.' });
    }

    if (startDate && endDate && endDate < startDate) {
      errs.push({ id: uuidv4(), title: 'Invalid Date Range', message: 'End date must be on or after the start date.' });
    }

    if (selectedTeamIds.length < 2) {
      errs.push({ id: uuidv4(), title: 'Teams Required', message: 'Tournament must have at least 2 participating teams.' });
    }

    if (leagueWinningPoint <= 0) {
      errs.push({ id: uuidv4(), title: 'Invalid League Winning Point', message: 'League winning point must be greater than 0.' });
    }

    if (leagueGoldenPoint <= leagueWinningPoint) {
      errs.push({ id: uuidv4(), title: 'Invalid League Golden Point', message: 'League golden point must be greater than league winning point.' });
    }

    if (playoffWinningPoint <= 0) {
      errs.push({ id: uuidv4(), title: 'Invalid Playoff Winning Point', message: 'Playoff winning point must be greater than 0.' });
    }

    if (playoffGoldenPoint <= playoffWinningPoint) {
      errs.push({ id: uuidv4(), title: 'Invalid Playoff Golden Point', message: 'Playoff golden point must be greater than playoff winning point.' });
    }

    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validate();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setErrors([]);

    try {
      if (isEditing) {
        await updateTournament(tournament.id, {
          name: name.trim(),
          startDate,
          endDate,
          venue: venue.trim() || undefined,
          leagueWinningPoint,
          leagueGoldenPoint,
          playoffWinningPoint,
          playoffGoldenPoint,
          teamIds: selectedTeamIds,
        });
      } else {
        await createTournament({
          name: name.trim(),
          startDate,
          endDate,
          venue: venue.trim() || undefined,
          leagueWinningPoint,
          leagueGoldenPoint,
          playoffWinningPoint,
          playoffGoldenPoint,
          teamIds: selectedTeamIds,
        });
      }
      onSuccess();
    } catch (err: any) {
      const errorMsg = err?.message || 'Could not save tournament. Please try again.';
      setErrors([{
        id: uuidv4(),
        title: 'Failed to Save',
        message: errorMsg.includes('must have at least 2') || errorMsg.includes('used in')
          ? errorMsg
          : 'Could not save tournament. Please try again.',
      }]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="px-4 py-4 border-b border-border">
            <h2 className="text-xl font-bold text-text-primary">
              {isEditing ? 'Edit Tournament' : 'Create Tournament'}
            </h2>
          </div>

          {/* Content */}
          <div className="px-4 py-4 space-y-4">
            {/* Tournament Name */}
            <div>
              <label className="block text-sm text-text-secondary mb-2">Tournament Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-field"
                placeholder="Enter tournament name"
              />
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm text-text-secondary mb-2">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-2">End Date</label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input-field w-full"
              />
              <p className="text-xs text-text-muted mt-1">
                {startDate && endDate
                  ? (endDate < startDate
                      ? 'End date must be on or after start date'
                      : endDate === startDate
                        ? 'Single-day tournament'
                        : 'Multi-day tournament')
                  : 'Same start and end date for a single-day tournament'}
              </p>
            </div>

            {/* Venue */}
            <div>
              <label className="block text-sm text-text-secondary mb-2">Venue (Optional)</label>
              <input
                type="text"
                value={venue}
                onChange={(e) => setVenue(e.target.value)}
                className="input-field"
                placeholder="Enter venue"
              />
            </div>

            {/* Participating Teams */}
            <div className="card bg-surface-raised">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                  Participating Teams
                </h3>
                <span className={`text-xs font-semibold ${
                  selectedTeamIds.length >= 2 ? 'text-success' : 'text-error'
                }`}>
                  {selectedTeamIds.length} selected (min 2)
                </span>
              </div>
              {loadingTeams ? (
                <div className="flex items-center justify-center py-4">
                  <span className="w-5 h-5 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {allTeams.map(team => (
                    <button
                      key={team.id}
                      type="button"
                      onClick={() => toggleTeamSelection(team.id)}
                      className={`w-full py-2 px-3 rounded-lg font-semibold transition-all flex items-center gap-3 ${
                        selectedTeamIds.includes(team.id)
                          ? 'bg-success/20 border-2 border-success text-success'
                          : 'bg-surface border border-border text-text-secondary hover:border-text-muted'
                      }`}
                    >
                      {team.logoUrl ? (
                        <img
                          src={team.logoUrl}
                          alt={team.shortName}
                          className="w-6 h-6 rounded object-contain flex-shrink-0"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <span
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: team.color }}
                        />
                      )}
                      <span className="font-semibold" style={{ color: team.color }}>
                        {team.shortName}
                      </span>
                      <span className="truncate text-text-secondary text-sm">
                        {team.name}
                      </span>
                      {selectedTeamIds.includes(team.id) && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="ml-auto">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {!loadingTeams && allTeams.length === 0 && (
                <p className="text-text-muted text-sm py-2">
                  No teams available. Create teams first.
                </p>
              )}
            </div>

            {/* League Rules */}
            <div className="card bg-surface-raised">
              <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
                League Rules
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Winning Point</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={leagueWinningPoint}
                    onChange={(e) => setLeagueWinningPoint(parseInt(e.target.value) || 0)}
                    className="input-field"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Golden Point</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={leagueGoldenPoint}
                    onChange={(e) => setLeagueGoldenPoint(parseInt(e.target.value) || 0)}
                    className="input-field"
                    min="1"
                  />
                </div>
              </div>
              <p className="text-xs text-text-muted mt-2">
                Default: {DEFAULT_TOURNAMENT_RULES.leagueWinningPoint} winning, {DEFAULT_TOURNAMENT_RULES.leagueGoldenPoint} golden
              </p>
            </div>

            {/* Playoff Rules */}
            <div className="card bg-surface-raised">
              <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">
                Playoff Rules
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Winning Point</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={playoffWinningPoint}
                    onChange={(e) => setPlayoffWinningPoint(parseInt(e.target.value) || 0)}
                    className="input-field"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-2">Golden Point</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={playoffGoldenPoint}
                    onChange={(e) => setPlayoffGoldenPoint(parseInt(e.target.value) || 0)}
                    className="input-field"
                    min="1"
                  />
                </div>
              </div>
              <p className="text-xs text-text-muted mt-2">
                Default: {DEFAULT_TOURNAMENT_RULES.playoffWinningPoint} winning, {DEFAULT_TOURNAMENT_RULES.playoffGoldenPoint} golden
              </p>
            </div>

            {/* Errors */}
            {errors.length > 0 && (
              <div className="bg-error/20 border border-error rounded-lg p-3">
                {errors.map((err) => (
                  <p key={err.id} className="text-error text-sm">{err.message}</p>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="px-4 py-4 border-t border-border space-y-3">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary bg-success text-white"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {isEditing ? 'SAVING...' : 'CREATING...'}
                </span>
              ) : (
                isEditing ? 'SAVE CHANGES' : 'CREATE TOURNAMENT'
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="btn-secondary"
            >
              CANCEL
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
